import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useOrgSettings } from '@/api/queries/useOrgSettings';
import { WorkEnvironmentTabsNav } from '@/components/work-environment/WorkEnvironmentTabsNav';
import { BliksLoader, Button, Page, PageHeader, PageContent } from '@bliks-io/ui';
import { LayoutGrid, Maximize2 } from 'lucide-react';
import { OrganigramCanvas } from './components/OrganigramCanvas';

export function OrganigramPage() {
  const { t } = useTranslation();
  const { data: departments, isLoading } = useDepartments();
  const { data: orgMembers, isLoading: membersLoading } = useOrgMembers({
    classification: 'internal'
  });
  const { data: organization, isLoading: organizationLoading } = useOrgSettings();

  useDocumentTitle('pageTitles.orgChart');

  // Fit view to show all nodes
  const handleFitView = useCallback(() => {
    // This will be handled by ReactFlow's fitView prop
    window.dispatchEvent(new Event('resize'));
  }, []);

  if (isLoading || membersLoading || organizationLoading) {
    return (
      <Page>
        <PageHeader>
          <WorkEnvironmentTabsNav />
        </PageHeader>
        <PageContent className="flex items-center justify-center">
          <BliksLoader label={t('common.loading')} />
        </PageContent>
      </Page>
    );
  }

  if (!departments || departments.length === 0) {
    return (
      <Page>
        <PageHeader>
          <WorkEnvironmentTabsNav />
        </PageHeader>
        <PageContent>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-xl font-semibold leading-7">
                {t('organization.organigram.title')}
              </h1>
              <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
                {t('organization.organigram.description')}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <LayoutGrid className="w-16 h-16 text-fg-disabled mb-4" />
            <p className="text-muted-foreground">{t('organization.organigram.empty')}</p>
          </div>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <WorkEnvironmentTabsNav />
      </PageHeader>
      <PageContent className="flex flex-col p-0">
        {/* Header */}
        <div className="px-10 pt-10 pb-4 bg-white border-b">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold leading-7">
                {t('organization.organigram.title')}
              </h1>
              <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
                {t('organization.organigram.description')}
              </p>
            </div>
            <Button
              variant="outline"
              size="medium"
              onClick={handleFitView}
              title={t('organization.organigram.fitView')}
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* React Flow Canvas */}
        <div className="flex-1 bg-white">
          <OrganigramCanvas
            departments={departments}
            orgMembers={orgMembers}
            organization={organization}
          />
        </div>
      </PageContent>
    </Page>
  );
}
